import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent } from '@material-ui/core'
import { Close, Add } from '@material-ui/icons'
import { Image, Spinner } from 'ui'
import { Button, TextField, Checkbox, TablePagination } from '@material-ui/core'
import { observer } from 'mobx-react'
import useStore from 'store'

import {
  DialogHeader,
  Header,
  CategoryListTitleContainer,
  CategoryListTitleSubContainer1,
  CategoryListTitleSubContainer2,
  HeaderTitle1,
  HeaderTitle2,
  ImageHeader,
  StyledIconButton,
  NewCategoryBody,
  NewCategoryContainerActions,
} from './CategoryModal.styles'

import SettingsIcon from 'assets/icons/ims/Settings.svg'
import User4Icon from 'assets/icons/ims/User4.svg'

// ── local styled components ──────────────────────────────────────────────────

const SaveButton = styled(Button)`
  display: flex;
  padding: 8.724px 23.631px 7.261px 23.992px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #155DFC;
  color: #FFF;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`
const CancelButton = styled(Button)`
  display: flex;
  padding: 9.634px 23.98px 8.17px 24.901px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 0.909px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
`
const TableHeadRow = styled.div`
  display: flex;
  align-items: center;
  background: rgba(241, 245, 249, 0.95);
  border-bottom: 0.909px solid #E2E8F0;
  padding: 10px 16px;
`
const TableHeadCell = styled.div<{ flex?: number }>`
  flex: ${p => p.flex ?? 1};
  color: #4A5565;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
`
const TableBodyRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 0.909px solid #F1F5F9;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #F8FAFC;
  }
`
const UserNameCell = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 8px;
  color: #101828;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`
const EmailInputCell = styled.div`
  flex: 1;
`
const StyledEmailInput = styled(TextField)`
  width: 100%;
  .MuiOutlinedInput-root {
    border-radius: 6px;
    background: #F8FAFC;
    font-family: Rubik;
    font-size: 14px;
  }
  .MuiOutlinedInput-input {
    padding: 8px 12px;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: #E2E8F0;
  }
`

// ── helpers ───────────────────────────────────────────────────────────────────

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

// ── types ────────────────────────────────────────────────────────────────────

type ManagerRow = {
  userId: number
  fullName: string
  protocolEmail: string
  selected: boolean
  emailError: boolean
}

// ── component ─────────────────────────────────────────────────────────────────

const SetMainManagerModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { protocolsStore, orgs } = useStore()
  const [rows, setRows] = useState<ManagerRow[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    if (!open) return
    const init = async () => {
      if (protocolsStore.baseUrl === '') {
        await protocolsStore.init(orgs.currentOrgId)
      }
      setLoading(true)
      const users = await protocolsStore.loadUserNames()
      setRows(
        users.map(u => ({
          userId: u.userId,
          fullName: u.fullName,
          protocolEmail: '',
          selected: false,
          emailError: false,
        }))
      )
      setLoading(false)
    }
    init()
  }, [open])

  const toggleSelected = (userId: number) => {
    setRows(prev =>
      prev.map(r => r.userId === userId
        ? { ...r, selected: !r.selected, emailError: !r.selected && !!r.protocolEmail && !isValidEmail(r.protocolEmail) }
        : r)
    )
  }

  const setEmail = (userId: number, email: string) => {
    setRows(prev =>
      prev.map(r => r.userId === userId
        ? { ...r, protocolEmail: email, emailError: r.selected && email.length > 0 && !isValidEmail(email) }
        : r)
    )
  }

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const hasSelected = rows.some(r => r.selected)
  const hasErrors = rows.some(r => r.selected && (r.emailError || !r.protocolEmail.trim()))

  const handleSave = async () => {
    const validated = rows.map(r => ({
      ...r,
      emailError: r.selected && !isValidEmail(r.protocolEmail.trim()),
    }))
    setRows(validated)
    if (validated.some(r => r.emailError)) return

    setSaving(true)
    const entries = validated
      .filter(r => r.selected)
      .map(r => ({ isMainManager: true, userId: r.userId, protocolEmail: r.protocolEmail.trim() }))
    const ok = await protocolsStore.saveMainManagers(entries)
    setSaving(false)
    if (ok) onClose()
  }

  return (
    <Dialog
      style={{ zIndex: 1200 }}
      open={open}
      fullWidth
       maxWidth="lg"
      scroll="paper"
    >
      <DialogHeader>
        <Header>
          <CategoryListTitleContainer>
            <CategoryListTitleSubContainer2>
              <ImageHeader src={User4Icon} />
            </CategoryListTitleSubContainer2>
            <CategoryListTitleSubContainer1>
              <HeaderTitle1>הגדרת מנהל ראשי</HeaderTitle1>
              <HeaderTitle2>בחר משתמשים והגדר כתובת דוא"ל לפרוטוקולים</HeaderTitle2>
            </CategoryListTitleSubContainer1>
          </CategoryListTitleContainer>
          <StyledIconButton onClick={onClose}>
            <Close style={{ color: '#364153' }} />
          </StyledIconButton>
        </Header>
      </DialogHeader>

      <DialogContent>
        <NewCategoryBody>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0', width: '100%' }}>
              <Spinner />
            </div>
          ) : null}
          <TableWrapper style={{ display: loading ? 'none' : 'flex' }}>
            <TableHeadRow>
              <TableHeadCell flex={1}>כתובת דוא"ל לפרוטוקול</TableHeadCell>
              <TableHeadCell flex={1}>שם משתמש</TableHeadCell>
              <TableHeadCell flex={0.1} />
            </TableHeadRow>

            {pagedRows.map(row => (
              <TableBodyRow key={row.userId}>
                <EmailInputCell>
                  <StyledEmailInput
                    variant="outlined"
                    size="small"
                    value={row.protocolEmail}
                    onChange={e => setEmail(row.userId, e.target.value)}
                    placeholder='דוא"ל'
                    disabled={!row.selected}
                    error={row.emailError}
                    helperText={row.emailError ? 'כתובת דוא"ל לא תקינה' : ''}
                  />
                </EmailInputCell>
                <UserNameCell>{row.fullName}</UserNameCell>
                <div style={{ flex: 0.1 }}>
                  <Checkbox
                    checked={row.selected}
                    onChange={() => toggleSelected(row.userId)}
                    style={{ padding: 4, color: '#155DFC' }}
                  />
                </div>
              </TableBodyRow>
            ))}
          </TableWrapper>
          {!loading && rows.length > 0 && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="שורות בעמוד"
            />
          )}
        </NewCategoryBody>
      </DialogContent>

      <NewCategoryContainerActions>
        <CancelButton onClick={onClose}>ביטול</CancelButton>
        <SaveButton onClick={handleSave} disabled={saving || !hasSelected || hasErrors}>
          שמור
        </SaveButton>
      </NewCategoryContainerActions>
    </Dialog>
  )
}

export default observer(SetMainManagerModal)
